import { apiPost, setTokenProvider, setTimerCancelerRegistrar, scheduleProactiveRefresh } from './api.js';
import { setState } from './store.js';

const TOKEN_KEY = 'lardr.token';
const USER_KEY  = 'lardr.user';

// Module-local in-memory fallback for when both Web Storage APIs are unavailable
// (e.g. Safari Private Mode with strict storage blocking).
let _memToken = null;
let _memUser  = null;

// Slot for a canceler callback registered by api.js (section 9). When clear()
// is called, this fires so any pending proactive-refresh setTimeout is aborted.
// Only api.js is expected to register; subsequent calls are silently ignored so
// a rogue caller cannot displace the real canceler.
let _refreshTimerCanceler = null;

export function setRefreshTimerCanceler(fn) {
  if (_refreshTimerCanceler === null) {
    _refreshTimerCanceler = fn;
  }
}

// ---------------------------------------------------------------------------
// tokenStore — dual-storage abstraction
// ---------------------------------------------------------------------------

function _trySetStorage(storage, key, value) {
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Write token to the appropriate backing store. One canonical token at a time:
 * writing to localStorage also removes the key from sessionStorage (and vice
 * versa) so stale copies cannot linger in the other store.
 * @param {string} tok
 * @param {boolean} remember  true → localStorage, false → sessionStorage (with in-memory fallback)
 */
export function set(tok, remember) {
  if (remember) {
    _trySetStorage(localStorage, TOKEN_KEY, tok);
    try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* storage blocked */ }
  } else {
    localStorage.removeItem(TOKEN_KEY);
    const wrote = _trySetStorage(sessionStorage, TOKEN_KEY, tok);
    if (!wrote) {
      _memToken = tok;
    }
  }
}

/**
 * Read the current JWT. Checks localStorage first (for Remember-Me sessions),
 * then sessionStorage, then the in-memory fallback.
 * @returns {string|null}
 */
export function token() {
  return localStorage.getItem(TOKEN_KEY)
    ?? sessionStorage.getItem(TOKEN_KEY)
    ?? _memToken;
}

export function isAuthenticated() {
  return !!token();
}

export function currentUser() {
  const raw = localStorage.getItem(USER_KEY) ?? sessionStorage.getItem(USER_KEY) ?? _memUser;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function _writeCredentials(tok, user, remember) {
  set(tok, remember);
  const userJson = JSON.stringify(user);
  if (remember) {
    _trySetStorage(localStorage, USER_KEY, userJson);
    try { sessionStorage.removeItem(USER_KEY); } catch { /* storage blocked */ }
  } else {
    localStorage.removeItem(USER_KEY);
    const wrote = _trySetStorage(sessionStorage, USER_KEY, userJson);
    if (!wrote) {
      _memUser = userJson;
    }
  }
}

export async function login(email, password, rememberMe = false) {
  const data = await apiPost('/auth/login', { email, password, remember_me: rememberMe });
  const user = {
    userId: data.user_id,
    householdId: data.household_id,
    role: data.role,
    expiresAt: data.expires_at,
  };
  _writeCredentials(data.token, user, rememberMe);
  setState({ user });
  scheduleProactiveRefresh(data.token);
  return user;
}

export async function logout() {
  try { await apiPost('/auth/logout', null); } catch { /* ignore */ }
  clear();
}

export function clear() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* storage blocked */ }
  try { sessionStorage.removeItem(USER_KEY); } catch { /* storage blocked */ }
  _memToken = null;
  _memUser  = null;
  if (_refreshTimerCanceler) {
    _refreshTimerCanceler();
  }
  setState({ user: null });
}

// Wire api.js callbacks at module load — main.js imports auth.js to trigger.
setTokenProvider(token);
setTimerCancelerRegistrar(setRefreshTimerCanceler);
